import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import type { ChatSummaryApi } from '@/modules/hiring-manager/chat/types';
import { useChatsQuery } from '@/modules/hiring-manager/chat/useChatsQuery';
import { useDeleteChatMutation } from '@/modules/hiring-manager/chat/useDeleteChatMutation';
import { ConfirmDialog } from '@/modules/ui/components/confirm-dialog/ConfirmDialog';
import {
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
} from '@/modules/ui/components/sidebar/Sidebar';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

interface ChatListProps {
  tenantId?: string;
  /** The tenant query string the portal threads through its links. */
  tenantSearch: string;
  /** The rail's row style, so chats line up with the pages above them. */
  itemClassName: string;
  /** Closes the mobile rail after a chat is opened. */
  onNavigate: () => void;
}

const chatTitle = (chat: ChatSummaryApi) => chat.title || 'Untitled chat';

/** The user's chats in the rail, newest activity first, with a new chat and deleting one. */
export function ChatList({
  tenantId,
  tenantSearch,
  itemClassName,
  onNavigate,
}: ChatListProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const chatsQuery = useChatsQuery(tenantId);
  const deleteMutation = useDeleteChatMutation();
  const [deleting, setDeleting] = useState<ChatSummaryApi | null>(null);

  const closeDialog = () => {
    setDeleting(null);
    deleteMutation.reset();
  };

  const deleteChat = (chat: ChatSummaryApi) =>
    deleteMutation.mutate(chat.id, {
      onSuccess: () => {
        closeDialog();
        if (location.pathname === `/hm/chat/${chat.id}`) {
          navigate(`/hm/chat${tenantSearch}`);
        }
      },
    });

  if (!tenantId) return null;

  return (
    <SidebarGroup className="px-0 pt-4">
      <SidebarGroupLabel className="h-7 px-2 text-xs">Chats</SidebarGroupLabel>
      <SidebarGroupAction asChild className="top-4.5 right-1" title="New chat">
        <Link onClick={onNavigate} to={`/hm/chat${tenantSearch}`}>
          <Plus />
          <span className="sr-only">New chat</span>
        </Link>
      </SidebarGroupAction>

      <SidebarMenu className="gap-0.5">
        {chatsQuery.isPending &&
          [0, 1, 2].map((row) => (
            <SidebarMenuItem key={row}>
              <SidebarMenuSkeleton />
            </SidebarMenuItem>
          ))}

        {chatsQuery.isError && chatsQuery.chats.length === 0 && (
          <SidebarMenuItem className="px-2 text-xs text-neutral-500">
            Couldn&apos;t load your chats.{' '}
            <button
              className="text-primary cursor-pointer font-medium hover:underline"
              onClick={() => void chatsQuery.refetch()}
              type="button"
            >
              Retry
            </button>
          </SidebarMenuItem>
        )}

        {chatsQuery.chats.map((chat) => (
          <SidebarMenuItem key={chat.id}>
            <SidebarMenuButton
              asChild
              className={itemClassName}
              isActive={location.pathname === `/hm/chat/${chat.id}`}
            >
              <Link
                onClick={onNavigate}
                title={chatTitle(chat)}
                to={`/hm/chat/${chat.id}${tenantSearch}`}
              >
                <span className="truncate">{chatTitle(chat)}</span>
              </Link>
            </SidebarMenuButton>
            <SidebarMenuAction
              aria-label={`Delete ${chatTitle(chat)}`}
              onClick={() => setDeleting(chat)}
              showOnHover
              title="Delete chat"
            >
              <Trash2 />
            </SidebarMenuAction>
          </SidebarMenuItem>
        ))}

        {chatsQuery.hasNextPage && (
          <SidebarMenuItem>
            <SidebarMenuButton
              className={itemClassName}
              disabled={chatsQuery.isFetchingNextPage}
              onClick={() => void chatsQuery.fetchNextPage()}
            >
              <span className="text-neutral-500">Show more</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        )}
      </SidebarMenu>

      <ConfirmDialog
        confirmText="Delete"
        confirming={deleteMutation.isPending}
        confirmingText="Deleting…"
        description="It leaves your chats and can't be opened again. Jobs it created or changed stay as they are."
        error={
          deleteMutation.isError
            ? actionErrorMessage('delete the chat', deleteMutation.error)
            : null
        }
        onCancel={closeDialog}
        onConfirm={() => deleting && deleteChat(deleting)}
        onOpenChange={(open) => !open && closeDialog()}
        open={deleting !== null}
        title="Delete this chat?"
        variant="destructive"
      />
    </SidebarGroup>
  );
}
